<script setup lang="ts">
import { computed, ref } from 'vue'

import type { DashboardStatsDTO, HangfireJobStatusDTO, UnsentNotificationsStatsDTO } from '@/types'

import JobStatusCard from '@/components/Admin/JobStatusCard.vue'
import UnsentNotificationsCard from '@/components/Admin/UnsentNotificationsCard.vue'
import UserStatsCard from '@/components/Admin/UserStatsCard.vue'
import { useActivityAwarePolling } from '@/composables/useActivityAwarePolling'
import { adminService } from '@/services/adminService'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

// Local state management
const dashboardStats = ref<DashboardStatsDTO | null>(null)
const jobStatus = ref<HangfireJobStatusDTO | null>(null)
const unsentNotifications = ref<UnsentNotificationsStatsDTO | null>(null)
const isLoadingDashboard = ref(false)
const isLoadingJobs = ref(false)
const isLoadingUnsent = ref(false)
const errorDashboard = ref<string | null>(null)
const errorJobs = ref<string | null>(null)
const errorUnsent = ref<string | null>(null)

// API functions
async function fetchDashboardStats() {
  isLoadingDashboard.value = true
  errorDashboard.value = null
  try {
    dashboardStats.value = await adminService.getDashboardStats()
  } catch (err) {
    console.error('Error fetching dashboard stats:', err)
    errorDashboard.value =
      err instanceof Error ? err.message : 'Failed to load dashboard statistics'
    dashboardStats.value = null
  } finally {
    isLoadingDashboard.value = false
  }
}

async function fetchJobStatus() {
  isLoadingJobs.value = true
  errorJobs.value = null
  try {
    jobStatus.value = await adminService.getJobStatus()
  } catch (err) {
    console.error('Error fetching job status:', err)
    errorJobs.value = err instanceof Error ? err.message : 'Failed to load job status'
    jobStatus.value = null
  } finally {
    isLoadingJobs.value = false
  }
}

async function fetchUnsentNotificationsStats() {
  isLoadingUnsent.value = true
  errorUnsent.value = null
  try {
    unsentNotifications.value = await adminService.getUnsentNotificationsStats()
  } catch (err) {
    console.error('Error fetching unsent notifications stats:', err)
    errorUnsent.value =
      err instanceof Error ? err.message : 'Failed to load unsent notifications statistics'
    unsentNotifications.value = null
  } finally {
    isLoadingUnsent.value = false
  }
}

// Combined refresh action
async function refreshAllStats() {
  // Run fetches in parallel
  await Promise.all([fetchDashboardStats(), fetchJobStatus(), fetchUnsentNotificationsStats()])
}

const autoRefreshEnabled = ref(true)
const refreshIntervalSeconds = ref(60) // Refresh every 60 seconds
const { countdown, refreshNow } = useActivityAwarePolling({
  enabled: autoRefreshEnabled,
  intervalSeconds: refreshIntervalSeconds,
  refresh: refreshAllStats,
})

// Combine errors for a general message if needed, or handle individually in cards
const overallError = computed(() => {
  if (errorDashboard.value && errorJobs.value) {
    return 'Failed to load dashboard statistics and job status.'
  }
  return errorDashboard.value || errorJobs.value // Show the first error encountered
})
</script>

<template>
  <div class="space-y-6 p-4">
    <div class="flex items-center justify-between">
      <h1 class="text-2xl font-semibold">Admin Dashboard</h1>
      <UButton
        icon="i-lucide-refresh-cw"
        label="Refresh"
        :loading="isLoadingDashboard || isLoadingJobs"
        size="sm"
        @click="refreshNow"
      />
    </div>

    <AppAlert v-if="overallError" color="error" :closable="false">
      {{ overallError }}
    </AppAlert>

    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <!-- User Statistics Card -->
      <UserStatsCard
        :stats="dashboardStats"
        :is-loading="isLoadingDashboard"
        :error="errorDashboard"
      />

      <!-- Job Status Card -->
      <JobStatusCard :status="jobStatus" :is-loading="isLoadingJobs" :error="errorJobs" />

      <!-- Unsent Notifications Card -->
      <UnsentNotificationsCard
        :stats="unsentNotifications"
        :is-loading="isLoadingUnsent"
        :error="errorUnsent"
      />

      <!-- Add more cards here in the future -->
    </div>

    <!-- Auto-refresh control -->
    <div class="text-muted-foreground mt-4 flex items-center justify-end space-x-2 text-sm">
      <UCheckbox v-model="autoRefreshEnabled" id="autoRefresh" />
      <label for="autoRefresh" class="cursor-pointer"
        >Auto-refresh every {{ refreshIntervalSeconds }} seconds</label
      >
      <span v-if="autoRefreshEnabled">(Next refresh in {{ countdown }}s)</span>
    </div>
  </div>
</template>

<style scoped>
.text-muted-foreground {
  color: var(--ui-text-muted);
}
</style>
